/**
 * 5段階フローの遷移(部門長確認・承認・支払い・完了確認・差戻し)。
 * 遷移条件は firestore.rules と二重に実装し、各遷移で関係者へ通知を作る。
 */
import {
  arrayUnion,
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { isValidAmount } from '@core/lib/money';
import { toMonth, tsToMillis } from '@core/lib/date';
import { writeAuditLog } from '@core/lib/auditLog';
import { newCashEntryId } from '@core/cash/cashRepo';
import { dedupHash } from '@core/cash/matching';
import { convertImagesToPdf } from '@core/expense/pdfTools';
import { markActionDone, notifyUsers } from './notificationsRepo';
import { notifyPayerAfterApproval, usersForNotification, usersForNotificationFrom } from './applicationRepo';
import { fetchUsersFromServer } from '@core/masters/masterRepo';
import { commitMain, runFollowUps, type FlowResult } from './flowResult';
import type {
  DateStr,
  DepartmentId,
  PaymentMethod,
  StoredFile,
  Transaction,
  User,
  Yen,
} from '@core/lib/types';

const COLLECTION = 'transactions';

function toTransaction(snap: QueryDocumentSnapshot): Transaction {
  return { id: snap.id, ...(snap.data() as object) } as Transaction;
}

// --- 各段階の担当分の取得 ----------------------------------------------------

/** ① 部門長確認待ち(部門長: 自分の事業部のみ) */
export async function fetchForManagerCheck(managerDepartmentIds: DepartmentId[]): Promise<Transaction[]> {
  if (managerDepartmentIds.length === 0) return [];
  // doc_type の絞り込みは不要: status='applied' になるのは申請(経費・請求書)だけ
  // (申請以外の取引は paid で直接作られる)。Firestoreは 'in' を1クエリ1つまで
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('department_id', 'in', managerDepartmentIds.slice(0, 30)),
      where('status', '==', 'applied'),
      limit(100),
    ),
  );
  return snap.docs.map(toTransaction).sort((a, b) => (a.posting_date < b.posting_date ? 1 : -1));
}

/** ② 承認待ち(承認者: 全事業部) */
export async function fetchForApproval(): Promise<Transaction[]> {
  const snap = await getDocs(
    query(collection(getDb(), COLLECTION), where('status', '==', 'manager_checked'), limit(100)),
  );
  return snap.docs.map(toTransaction).sort((a, b) => (a.posting_date < b.posting_date ? 1 : -1));
}

/** ③ 支払い待ち(経理: 経理依頼分のみ) */
export async function fetchForPaymentByAccounting(): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('status', '==', 'approved'),
      where('payer', '==', 'accounting'),
      limit(300),
    ),
  );
  return snap.docs.map(toTransaction).sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/**
 * ③ 支払い待ち(部門長: 自分が部門長を務める事業部の「部門長に依頼」分のみ)。
 * 現金フローの受領待ち(cash_ready)・受領済み(received)も部門長の担当として含める
 */
export async function fetchForPaymentByBucho(managerDepartmentIds: DepartmentId[]): Promise<Transaction[]> {
  if (managerDepartmentIds.length === 0) return [];
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('department_id', 'in', managerDepartmentIds.slice(0, 30)),
      where('payer', '==', 'bucho'),
      limit(200),
    ),
  );
  return snap.docs
    .map(toTransaction)
    .filter((t) => ['approved', 'cash_ready', 'received'].includes(t.status))
    .sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/**
 * ③ 支払い待ち(承認者 — 部門長役の代行分。p15・2026-08-26)。
 * 部門長がいない事業部の「部門長に依頼/部門長立替」(承認者・経理本人の申請等)を、
 * もう一方の承認者が部門長役として支払う。自分の申請は対象外。
 * 申請者が管理側(部門長・承認者・経理)かどうかの絞り込みは呼び出し側で行う
 */
export async function fetchForPaymentByApprover(userId: string): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('status', '==', 'approved'),
      where('payer', '==', 'bucho'),
      limit(100),
    ),
  );
  return snap.docs
    .map(toTransaction)
    .filter((t) => t.created_by !== userId)
    .sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/** 仮払い待ち(経理)。仮払いの承認済み申請(v1.1 §3-C-4) */
export async function fetchForCashHandover(): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('payment_method', '==', 'prepaid'),
      where('status', '==', 'approved'),
      limit(100),
    ),
  );
  return snap.docs.map(toTransaction).sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/**
 * 支払いページの「自分の支払い」(2026-08-26): 自分が支払うべき自分の申請。
 * 承認済み(クレカ・立替)と、仮払いの受領済み(仮払い金で支払う)が対象
 */
export async function fetchForSelfPayment(userId: string): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('created_by', '==', userId),
      where('status', 'in', ['approved', 'received']),
      limit(100),
    ),
  );
  return snap.docs
    .map(toTransaction)
    .filter(
      (t) =>
        t.payer === 'self' &&
        // 仮払いは受領後に支払う(approvedの間は経理の仮払い待ち)。現金は経理支払いのみ
        (t.status === 'received' || (t.payment_method !== 'prepaid' && t.payment_method !== 'cash')),
    )
    .sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/**
 * 受領ページ(2026-08-26 ページ分割): 自分が受領を記録すべき申請。
 * - 仮払い: 自分の申請で経理の仮払い済み(cash_ready)→ 記入済み受領書を添付して受領を記録
 * - 立替: 自分が立替払いし経理の精算が済んだもの → 記入済み受領書を添付(部門長立替は担当事業部から探す)
 */
export async function fetchForReceive(
  userId: string,
  managerDepartmentIds: DepartmentId[],
  /** 承認者・経理は全件読めるため、部門長役で立替した分(paid_by=自分)も全事業部から探す */
  canReadAll = false,
): Promise<Transaction[]> {
  const db = getDb();
  const [prepaidSnap, ownPaidSnap, deptPaidSnap, allPaidSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, COLLECTION),
        where('created_by', '==', userId),
        where('status', '==', 'cash_ready'),
        limit(100),
      ),
    ),
    getDocs(
      query(
        collection(db, COLLECTION),
        where('created_by', '==', userId),
        where('status', '==', 'paid'),
        where('advance', '==', true),
        limit(100),
      ),
    ),
    managerDepartmentIds.length > 0
      ? getDocs(
          query(
            collection(db, COLLECTION),
            where('department_id', 'in', managerDepartmentIds.slice(0, 30)),
            where('status', '==', 'paid'),
            where('advance', '==', true),
            limit(100),
          ),
        )
      : Promise.resolve(null),
    canReadAll
      ? getDocs(
          query(
            collection(db, COLLECTION),
            where('status', '==', 'paid'),
            where('advance', '==', true),
            limit(100),
          ),
        )
      : Promise.resolve(null),
  ]);

  const map = new Map<string, Transaction>();
  for (const d of prepaidSnap.docs) {
    const t = toTransaction(d);
    if (t.payer === 'self') map.set(t.id, t);
  }
  // 精算済み・本人の受領書が未添付のもの
  const needsSettle = (t: Transaction) =>
    t.paid_by === userId && t.reimbursed_at != null && t.settlement_received_at == null;
  for (const d of ownPaidSnap.docs) {
    const t = toTransaction(d);
    if (needsSettle(t)) map.set(t.id, t);
  }
  if (deptPaidSnap) {
    for (const d of deptPaidSnap.docs) {
      const t = toTransaction(d);
      if (needsSettle(t)) map.set(t.id, t);
    }
  }
  if (allPaidSnap) {
    for (const d of allPaidSnap.docs) {
      const t = toTransaction(d);
      if (needsSettle(t)) map.set(t.id, t);
    }
  }
  return [...map.values()].sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

// --- 繰り返し支払いの部門長確認(2026-10-15 発注者指示) ---------------------------------
// 流れ: 経理が支払う → その事業部の部門長が確認 → 承認者が完了確認 → 完了。
// 部門長がいない事業部(または部門長が支払った経理本人だけ)の分は、部門長確認を飛ばして承認者へ進む。

/**
 * この時刻より前に支払った分は、従来どおり部門長確認なしで承認者の完了確認へ進める
 * (本番反映の時点で、すでに支払い済みで完了確認待ちだった分を途中で部門長側へ移さないため)
 */
export const RECURRING_MANAGER_CHECK_FROM_MS = Date.parse('2026-10-15T09:00:00+09:00');

/** 繰り返し支払いを確認する部門長(その事業部の部門長。支払った本人は除く) */
export function recurringManagersFor(t: Pick<Transaction, 'department_id' | 'paid_by'>, users: User[]): string[] {
  return usersForNotificationFrom(users, 'bucho', t.department_id).filter((id) => id !== t.paid_by);
}

/** 繰り返し支払いで、部門長確認がまだ必要か */
export function recurringNeedsManagerCheck(t: Transaction, users: User[]): boolean {
  if (t.source !== 'recurring' || t.status !== 'paid' || t.manager_checked_at) return false;
  if (tsToMillis(t.paid_at) < RECURRING_MANAGER_CHECK_FROM_MS) return false;
  return recurringManagersFor(t, users).length > 0;
}

/** 部門長の「承認」画面に出す、確認待ちの繰り返し支払い(自分が確認する事業部の分) */
export async function fetchRecurringForManagerCheck(userId: string, managerDepartmentIds: DepartmentId[]): Promise<Transaction[]> {
  if (managerDepartmentIds.length === 0) return [];
  const [snap, users] = await Promise.all([
    getDocs(
      query(
        collection(getDb(), COLLECTION),
        where('source', '==', 'recurring'),
        where('status', '==', 'paid'),
        where('department_id', 'in', managerDepartmentIds.slice(0, 30)),
        limit(200),
      ),
    ),
    fetchUsersFromServer(),
  ]);
  return snap.docs
    .map(toTransaction)
    .filter((t) => recurringNeedsManagerCheck(t, users) && recurringManagersFor(t, users).includes(userId))
    .sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/** 部門長が繰り返し支払いを確認する(確認の印だけを書く)。次は承認者の完了確認 */
export async function recurringManagerCheck(params: { transaction: Transaction; userId: string }): Promise<FlowResult> {
  const { transaction, userId } = params;
  if (transaction.source !== 'recurring' || transaction.status !== 'paid') {
    throw new Error('この支払いは部門長確認の対象ではありません');
  }
  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      manager_checked_at: serverTimestamp(),
      manager_checked_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );
  return runFollowUps([
    // 部門長宛ての確認依頼(ほかの部門長の分も)を消す
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '承認者への通知',
      async () =>
        notifyUsers({
          userIds: await usersForNotification('approver'),
          transactionId: transaction.id,
          message: `${transaction.code} の完了確認をお願いします(繰り返し支払い・部門長確認済み)`,
          kind: 'action',
          stage: 'confirm',
        }),
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'recurring_manager_check', by: userId }),
    ],
  ]);
}

/**
 * 自分のお金の戻り(2026-10-15 発注者指示: 「受領待ち」「受領」を、申請者が自分のお金の状況を
 * すぐ見られる画面に作り直す)。
 * - 受領待ち: 自分が立て替えて支払い済みで、まだ経理の精算が済んでいないもの
 *             + 経理から仮払い金を渡されて、受け取りの記録がまだのもの(自分の作業)
 * - 受領    : 精算が済んで、お金が戻ってきたもの + 仮払い金を受け取ったもの(履歴)
 * 「自分が立て替えた」は支払った人(paid_by)で判定する(部門長が部下の申請を立て替えた場合も含む)。
 * 読める範囲は権限ルールに合わせて取る(自分の申請 / 担当事業部 / 承認者・経理は全件)
 */
export async function fetchMyMoneyBack(
  userId: string,
  managerDepartmentIds: DepartmentId[],
  canReadAll = false,
): Promise<{ waiting: Transaction[]; received: Transaction[] }> {
  const db = getDb();
  const col = collection(db, COLLECTION);
  const [ownAdv, deptAdv, allAdv, ownPrepaid] = await Promise.all([
    getDocs(query(col, where('created_by', '==', userId), where('advance', '==', true), limit(300))),
    managerDepartmentIds.length > 0
      ? getDocs(
          query(col, where('department_id', 'in', managerDepartmentIds.slice(0, 30)), where('advance', '==', true), limit(300)),
        )
      : Promise.resolve(null),
    canReadAll ? getDocs(query(col, where('paid_by', '==', userId), where('advance', '==', true), limit(300))) : Promise.resolve(null),
    getDocs(query(col, where('created_by', '==', userId), where('payment_method', '==', 'prepaid'), limit(300))),
  ]);

  const adv = new Map<string, Transaction>();
  for (const snap of [ownAdv, deptAdv, allAdv]) {
    for (const d of snap?.docs ?? []) {
      const t = toTransaction(d);
      if (t.paid_by === userId) adv.set(t.id, t);
    }
  }
  const waiting: Transaction[] = [];
  const received: Transaction[] = [];
  for (const t of adv.values()) {
    if (t.status === 'cancelled' || t.status === 'rejected') continue;
    if (t.reimbursed_at) received.push(t);
    else if (t.status === 'paid') waiting.push(t);
  }
  for (const d of ownPrepaid.docs) {
    const t = toTransaction(d);
    if (t.payer !== 'self' || t.status === 'cancelled' || t.status === 'rejected') continue;
    if (t.status === 'cash_ready') waiting.push(t);
    else if (t.received_at) received.push(t);
  }
  // 受領待ちは古いもの(待たされている順)から、受領は新しいものから
  waiting.sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
  received.sort((a, b) => (moneyBackDate(a) < moneyBackDate(b) ? 1 : -1));
  return { waiting, received };
}

/** お金が戻ってきた日(立替=精算日 / 仮払い=受領日) */
export function moneyBackDate(t: Transaction): string {
  if (t.reimbursed_at) return t.reimbursement_date ?? '';
  const ms = t.received_at && typeof (t.received_at as { toMillis?: () => number }).toMillis === 'function'
    ? (t.received_at as { toMillis: () => number }).toMillis()
    : 0;
  return ms ? new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 10) : '';
}

/**
 * ④ 完了確認待ち(承認者: 支払い済みの申請)。
 * 報告型(支払い済み申請)も同じ画面に表示する(2026-08-27。承認者確認=完了)
 */
export async function fetchForCompletion(): Promise<Transaction[]> {
  const [snap, recurring] = await Promise.all([
    getDocs(
      query(
        collection(getDb(), COLLECTION),
        where('status', '==', 'paid'),
        where('doc_type', 'in', ['expense', 'invoice']),
        limit(100),
      ),
    ),
    // 繰り返し支払い(2026-09-07): 経理が支払ったあと、承認者が完了確認でロックする
    getDocs(
      query(collection(getDb(), COLLECTION), where('status', '==', 'paid'), where('source', '==', 'recurring'), limit(100)),
    ),
  ]);
  const seen = new Set<string>();
  // 繰り返し支払いは部門長確認が済んでから(2026-10-15)。確認が要るかは名簿で決めるため、必ずサーバーから読む
  const users = recurring.empty ? [] : await fetchUsersFromServer();
  return [...snap.docs, ...recurring.docs]
    .map(toTransaction)
    .filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)))
    .filter((t) => !recurringNeedsManagerCheck(t, users))
    .sort((a, b) => (a.payment_date < b.payment_date ? 1 : -1));
}

// --- 報告型(経費申請(承認不要)・2026-08-26 会議 spec v1.2 §3-D) ---------------

/**
 * 支払い済み申請の一覧(報告型)。経理・承認者・管理者は全件、それ以外は自分の分。
 * 確認状況(経理確認/承認者確認の未・済)を表示するために使う
 */
export async function fetchReports(userId: string, seesAll: boolean): Promise<Transaction[]> {
  const snap = await getDocs(
    seesAll
      ? query(collection(getDb(), COLLECTION), where('source', '==', 'report'), limit(200))
      : query(
          collection(getDb(), COLLECTION),
          where('source', '==', 'report'),
          where('created_by', '==', userId),
          limit(200),
        ),
  );
  return snap.docs
    .map(toTransaction)
    .sort((a, b) => (a.payment_date < b.payment_date ? 1 : -1));
}

/** 経理確認待ちの支払い済み申請(経理確認ページ・2026-08-27) */
export async function fetchForReportAccountingConfirm(): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('source', '==', 'report'),
      where('status', '==', 'paid'),
      where('report_accounting_confirmed_at', '==', null),
      limit(100),
    ),
  );
  return snap.docs.map(toTransaction).sort((a, b) => (a.payment_date < b.payment_date ? 1 : -1));
}

/** 報告型の経理確認。確認済みフラグのみ更新する */
export async function reportAccountingConfirm(params: {
  transaction: Transaction;
  userId: string;
}): Promise<FlowResult> {
  const { transaction, userId } = params;
  if (transaction.source !== 'report' || transaction.status !== 'paid') {
    throw new Error('この申請は経理確認できる状態ではありません');
  }
  if (transaction.report_accounting_confirmed_at) throw new Error('既に経理確認済みです');
  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      report_accounting_confirmed_at: serverTimestamp(),
      report_accounting_confirmed_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );
  return runFollowUps([
    // 経理確認が済んだので、経理確認ページ宛ての依頼をまとめて既読にする(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '申請者への通知',
      async () => {
        if (transaction.created_by === userId) return;
        await notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} の経費報告が経理確認されました。承認者確認待ちです`,
        });
      },
    ],
    // 次は承認者確認(④完了確認ページ)。操作依頼として通知する(2026-08-27)
    [
      '承認者への通知',
      async () =>
        notifyUsers({
          userIds: (await usersForNotification('approver')).filter((id) => id !== userId),
          transactionId: transaction.id,
          message: `${transaction.code} の承認者確認をお願いします(支払い済み申請・経理確認済み)`,
          kind: 'action',
          stage: 'confirm',
        }),
    ],
    [
      '操作履歴の記録',
      () =>
        writeAuditLog({
          targetType: 'transaction',
          targetId: transaction.id,
          action: 'report-accounting-confirm',
          by: userId,
        }),
    ],
  ]);
}

/** 報告型の承認者確認(経理確認の後)。completed にしてロックする */
export async function reportApproverConfirm(params: {
  transaction: Transaction;
  userId: string;
}): Promise<FlowResult> {
  const { transaction, userId } = params;
  if (transaction.source !== 'report' || transaction.status !== 'paid') {
    throw new Error('この申請は承認者確認できる状態ではありません');
  }
  if (!transaction.report_accounting_confirmed_at) {
    throw new Error('先に経理確認が必要です(経理確認→承認者確認の順)');
  }
  const pdfPatch = await convertImagesToPdf(transaction, userId);
  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      ...pdfPatch,
      status: 'completed',
      completed_at: serverTimestamp(),
      completed_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );
  return runFollowUps([
    // 承認者確認が済んだので、完了確認宛ての依頼をまとめて既読にする(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '申請者への通知',
      async () => {
        if (transaction.created_by === userId) return;
        await notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} の経費報告が承認者確認されました(完了)`,
        });
      },
    ],
    [
      '操作履歴の記録',
      () =>
        writeAuditLog({
          targetType: 'transaction',
          targetId: transaction.id,
          action: 'report-approver-confirm',
          by: userId,
        }),
    ],
  ]);
}

// --- 遷移 ---------------------------------------------------------------------

/**
 * ① 部門長確認(applied → manager_checked)。部門長のみ。
 * advance=true(部門長立替)を選ぶと、支払い者=部門長・支払方法=立替の立替型に切り替わる(v1.1 ⑦)
 */
export async function managerCheck(params: {
  transaction: Transaction;
  userId: string;
  advance?: boolean;
}): Promise<FlowResult> {
  const { transaction, userId, advance = false } = params;
  if (transaction.status !== 'applied') throw new Error('この申請は部門長確認できる状態ではありません');

  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      status: 'manager_checked',
      manager_checked_at: serverTimestamp(),
      manager_checked_by: userId,
      // 部門長立替への転換: 立替型(支払方法=立替)として部門長が支払い、後で経理が精算する
      ...(advance ? { payer: 'bucho', advance: true, payment_method: 'reimbursement' } : {}),
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );

  return runFollowUps([
    // 部門長確認が済んだので、部門長宛ての操作依頼をまとめて既読にする(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '承認者への通知',
      async () =>
        notifyUsers({
          userIds: await usersForNotification('approver'),
          transactionId: transaction.id,
          message: `${transaction.code} の承認をお願いします`,
          kind: 'action',
          stage: 'approve',
        }),
    ],
    [
      '申請者への通知',
      async () => {
        if (transaction.created_by === userId) return;
        await notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} が部門長確認されました。承認者の承認待ちです`,
        });
      },
    ],
    [
      '操作履歴の記録',
      () =>
        writeAuditLog({
          targetType: 'transaction',
          targetId: transaction.id,
          action: 'manager-check',
          by: userId,
        }),
    ],
  ]);
}

/**
 * ② 承認(manager_checked → approved)。承認者のみ・どちらか1名でよい。
 * advance=true(部門長立替)を選ぶと、支払い者を部門長・立替扱いにして承認する(v3.8)
 */
export async function approve(params: {
  transaction: Transaction;
  userId: string;
  advance?: boolean;
}): Promise<FlowResult> {
  const { transaction, userId, advance = false } = params;
  if (transaction.status !== 'manager_checked') throw new Error('この申請は承認できる状態ではありません');
  if (transaction.created_by === userId) {
    throw new Error('自分の申請は自分で承認できません(もう一方の承認者が承認します)');
  }

  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      status: 'approved',
      approved_at: serverTimestamp(),
      approved_by: userId,
      ...(advance ? { payer: 'bucho', advance: true, payment_method: 'reimbursement' } : {}),
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );

  const payerAfter = advance ? 'bucho' : transaction.payer;
  const methodAfter = advance ? 'reimbursement' : transaction.payment_method;
  return runFollowUps([
    // 承認が済んだので、承認者宛ての操作依頼をまとめて既読にする(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '支払い者への通知',
      () =>
        notifyPayerAfterApproval({
          id: transaction.id,
          code: transaction.code,
          departmentId: transaction.department_id,
          createdBy: transaction.created_by,
          payer: payerAfter,
          advance: advance || transaction.advance === true,
          paymentMethod: methodAfter,
        }),
    ],
    [
      '申請者への通知',
      async () => {
        // お知らせ(取引ごとに1行で上書き): 次のステップも書き添える(2026-08-27)。
        // 支払い者=自分(仮払い以外)は、支払い依頼の通知が本人へ届く(同文)ため二重に上書きしない
        if (payerAfter === 'self' && methodAfter !== 'prepaid') return;
        const nextHint =
          methodAfter === 'prepaid'
            ? '経理の仮払い待ちです'
            : payerAfter === 'bucho'
              ? '部門長の支払い待ちです'
              : '経理の支払い待ちです';
        await notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} が承認されました。${nextHint}`,
        });
      },
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'approve', by: userId }),
    ],
  ]);
}

export type PaymentInput = {
  paymentDate: DateStr;
  paymentMethod: PaymentMethod;
  /** 実際に支払った口座・現金・カード(口座マスタ。記録用・v3.7) */
  paymentAccountId?: string | null;
  /** 振込明細・領収書(必須) */
  receipts: StoredFile[];
  /** CFに登録する支払口座(経理支払いのときのみ) */
  cashAccountId: string | null;
  /** 経理は金額・科目・計上日を補正できる(請求書の経理入力欄) */
  amount?: Yen;
  accountId?: string;
  subAccountId?: string;
  postingDate?: DateStr;
  /** クレカの購入日(2026-09-04)。paymentDate には引き落とし日が入る */
  purchaseDate?: DateStr | null;
  /** CFへの出金の状態(2026-09-07)。クレカは引き落とし日がこれからなので 'planned'(予定) */
  cashEntryStatus?: 'planned' | 'settled';
  /** 振込手数料(2026-08-26 会議)。振込×経理のみ。「支払手数料」科目の別取引として自動計上 */
  transferFee?: Yen | null;
  /** 手数料を計上する科目(支払手数料)のID */
  feeAccountId?: string | null;
};

/**
 * 仮払い支払(approved → cash_ready)。仮払いのみ・経理(v1.1 §3-C-4)。
 * 経理が本人へ仮払いし、CFへ出金(確定)を計上する。受領書を印刷して本人へ渡す
 */
export async function handCash(params: {
  transaction: Transaction;
  userId: string;
  cashAccountId: string;
}): Promise<FlowResult> {
  const { transaction, userId, cashAccountId } = params;
  if (transaction.status !== 'approved' || transaction.payment_method !== 'prepaid') {
    throw new Error('この申請は仮払いの対象ではありません');
  }
  if (!cashAccountId) throw new Error('仮払いに使う口座・現金を選んでください');

  const db = getDb();
  const batch = writeBatch(db);
  const cashEntryId = newCashEntryId();
  const today = new Date();
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  batch.set(doc(db, 'cash_entries', cashEntryId), {
    cash_account_id: cashAccountId,
    date: dateStr,
    direction: 'out',
    amount: transaction.amount,
    status: 'settled',
    description: `仮払い ${transaction.code ?? ''}`,
    counterparty: null,
    transaction_id: transaction.id,
    import_batch_id: null,
    dedup_hash: dedupHash({
      cashAccountId,
      date: dateStr,
      direction: 'out',
      amount: transaction.amount,
      description: `仮払い ${transaction.code ?? ''}`,
    }),
    matched_at: null,
    matched_by: null,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  batch.update(doc(db, COLLECTION, transaction.id), {
    status: 'cash_ready',
    cash_handed_at: serverTimestamp(),
    cash_handed_by: userId,
    prepaid_cash_entry_id: cashEntryId,
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
  await commitMain([transaction], () => batch.commit());

  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '申請者への通知',
      () =>
        notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} の仮払い金を経理から受け取り、受領書に記入して「受領待ち」画面から記録してください`,
          kind: 'action',
          stage: 'receive',
        }),
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'prepay', by: userId }),
    ],
  ]);
}

/** 受領(cash_ready → received)。本人のみ。記入済み受領書の添付が必須(v1.1 §3-C-5) */
export async function confirmReceive(params: {
  transaction: Transaction;
  userId: string;
  receipts: StoredFile[];
}): Promise<FlowResult> {
  const { transaction, userId, receipts } = params;
  if (transaction.status !== 'cash_ready') throw new Error('この申請は受領を記録できる状態ではありません');
  if (receipts.length === 0) throw new Error('記入済みの受領書の添付が必要です');
  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      status: 'received',
      received_at: serverTimestamp(),
      received_by: userId,
      received_receipts: receipts,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );
  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    // 受領完了 → 次は本人の支払い。支払いページへの操作依頼として通知する(2026-08-26)
    [
      '支払いの依頼の通知',
      () =>
        notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} の受領を記録しました。仮払い金で支払いを実行し、「支払い待ち」画面から登録してください`,
          kind: 'action',
          stage: 'pay',
        }),
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'cash-receive', by: userId }),
    ],
  ]);
}

/**
 * 立替型の受領(v1.1 §3-B-6)。経理の精算後、立替した本人が記入済み受領書を添付する。
 * これが済むと承認者の完了確認に進める
 */
export async function settleReceive(params: {
  transaction: Transaction;
  userId: string;
  receipts: StoredFile[];
}): Promise<FlowResult> {
  const { transaction, userId, receipts } = params;
  if (transaction.status !== 'paid' || transaction.advance !== true || !transaction.reimbursed_at) {
    throw new Error('この申請は精算の受領を記録できる状態ではありません');
  }
  if (receipts.length === 0) throw new Error('記入済みの受領書の添付が必要です');
  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      settlement_receipts: receipts,
      settlement_received_at: serverTimestamp(),
      settlement_received_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );
  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '承認者への通知',
      async () =>
        notifyUsers({
          userIds: await usersForNotification('approver'),
          transactionId: transaction.id,
          message: `${transaction.code} の完了確認をお願いします(立替精算・受領書の添付済み)`,
          kind: 'action',
          stage: 'confirm',
        }),
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'settle-receive', by: userId }),
    ],
  ]);
}

/** ③ 支払い(approved/received → paid)。**ここで実績計上**。証憑(署名済み受領書・領収書)必須 */
export async function pay(params: {
  transaction: Transaction;
  input: PaymentInput;
  userId: string;
}): Promise<FlowResult> {
  const { transaction, input, userId } = params;
  if (!['approved', 'received'].includes(transaction.status)) {
    throw new Error('この申請は支払いできる状態ではありません');
  }
  // 仮払いは、経理の仮払い→受領を経てから支払う(v1.1 §3-C)
  if (transaction.payment_method === 'prepaid' && transaction.status !== 'received') {
    throw new Error('仮払いの支払いは、経理の仮払いと受領の記録が済んでから行えます');
  }
  // 仮払いは差額が発生しない運用(仮払い額=確定金額)
  if (transaction.payment_method === 'prepaid' && (input.amount ?? transaction.amount) !== transaction.amount) {
    throw new Error('仮払いは仮払い額と同額で支払います(差額なし運用)');
  }
  // 繰り返し支払い(2026-09-07): 口座引落・振込の定期支払いは明細の添付を任意にする。
  // 現金払い(支払者=経理)も任意(2026-09-17 改修依頼 D。firestore.rules の payAction と同じ条件)
  const receiptOptional =
    transaction.source === 'recurring' || (transaction.payment_method === 'cash' && transaction.payer === 'accounting');
  if (input.receipts.length === 0 && !receiptOptional) {
    throw new Error('署名済み受領書または領収書の添付が必要です');
  }
  const amount = input.amount ?? transaction.amount;
  if (!isValidAmount(amount)) throw new Error('金額は1円以上の整数で入力してください');
  const accountId = input.accountId ?? transaction.account_id;
  if (!accountId) throw new Error('科目①を入力してください(経理入力欄)');
  const postingDate = input.postingDate ?? transaction.posting_date;

  const db = getDb();
  const batch = writeBatch(db);
  let cashEntryId: string | null = null;

  // 繰り返し支払い(2026-09-07): 支払い待ちを作った時点でCFに「予定」の出金がある。
  // 新しく作らず、その予定を実際の金額・日付で「確定」に更新する
  if (transaction.source === 'recurring' && transaction.cash_entry_id) {
    cashEntryId = transaction.cash_entry_id;
    batch.update(doc(db, 'cash_entries', cashEntryId), {
      ...(input.cashAccountId ? { cash_account_id: input.cashAccountId } : {}),
      date: input.paymentDate,
      amount,
      status: 'settled',
      updated_at: serverTimestamp(),
    });
  } else if (input.cashAccountId) {
    // 実際にお金が動いた時点のため、CFへは確定(settled)で登録する(decisions.md 2026-08-21)
    cashEntryId = newCashEntryId();
    batch.set(doc(db, 'cash_entries', cashEntryId), {
      cash_account_id: input.cashAccountId,
      date: input.paymentDate,
      direction: 'out',
      amount,
      // クレカは引き落とし日(未来)の予定として記録する。現金・振込は動いた時点なので確定
      status: input.cashEntryStatus ?? 'settled',
      description: transaction.description ?? transaction.code ?? '経費支払',
      counterparty: null,
      transaction_id: transaction.id,
      import_batch_id: null,
      dedup_hash: dedupHash({
        cashAccountId: input.cashAccountId,
        date: input.paymentDate,
        direction: 'out',
        amount,
        description: transaction.description ?? '',
      }),
      matched_at: null,
      matched_by: null,
      created_by: userId,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
  }

  batch.update(doc(db, COLLECTION, transaction.id), {
    status: 'paid',
    amount,
    account_id: accountId,
    sub_account_id: input.subAccountId ?? transaction.sub_account_id,
    posting_date: postingDate,
    posting_month: toMonth(postingDate),
    payment_date: input.paymentDate,
    payment_month: toMonth(input.paymentDate),
    payment_method: input.paymentMethod,
    payment_account_id: input.paymentAccountId ?? null,
    purchase_date: input.purchaseDate ?? null,
    receipts: [...(transaction.receipts ?? []), ...input.receipts],
    cash_entry_id: cashEntryId,
    transfer_fee: input.transferFee ?? null,
    paid_at: serverTimestamp(),
    paid_by: userId,
    updated_at: serverTimestamp(),
    updated_by: userId,
  });

  // 振込手数料(2026-08-26 会議): 「支払手数料」科目・同じ事業部の別取引として自動計上し、
  // CFにも同じ口座から出金として登録する
  const fee = input.transferFee ?? 0;
  if (fee > 0 && input.feeAccountId) {
    let feeCashEntryId: string | null = null;
    if (input.cashAccountId) {
      feeCashEntryId = newCashEntryId();
      batch.set(doc(db, 'cash_entries', feeCashEntryId), {
        cash_account_id: input.cashAccountId,
        date: input.paymentDate,
        direction: 'out',
        amount: fee,
        status: 'settled',
        description: `振込手数料 ${transaction.code ?? ''}`,
        counterparty: null,
        transaction_id: transaction.id,
        import_batch_id: null,
        dedup_hash: dedupHash({
          cashAccountId: input.cashAccountId,
          date: input.paymentDate,
          direction: 'out',
          amount: fee,
          description: `振込手数料 ${transaction.code ?? ''}`,
        }),
        matched_at: null,
        matched_by: null,
        created_by: userId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
    }
    batch.set(doc(collection(db, COLLECTION)), {
      type: 'expense',
      doc_type: null,
      status: 'paid',
      source: 'transfer_fee',
      fee_of: transaction.id,
      code: `${transaction.code ?? ''}-手数料`,
      company_id: transaction.company_id ?? 'sum',
      department_id: transaction.department_id,
      hierarchy_node_id: transaction.hierarchy_node_id ?? null,
      account_id: input.feeAccountId,
      sub_account_id: '',
      fiscal_year: transaction.fiscal_year ?? null,
      amount: fee,
      applied_amount: fee,
      tax_mode: 'inclusive',
      tax_amount: null,
      posting_date: input.paymentDate,
      posting_month: toMonth(input.paymentDate),
      payment_date: input.paymentDate,
      payment_month: toMonth(input.paymentDate),
      due_date: null,
      payment_method: 'bank_transfer',
      payment_account_id: input.paymentAccountId ?? null,
      payer: 'accounting',
      counterparty: null,
      vendor_id: null,
      description: `振込手数料(${transaction.code ?? ''})`,
      note: null,
      attachments: [],
      receipts: [],
      approvals: [],
      approval_snapshot: [],
      approval_rule_id: '',
      current_step: 0,
      is_internal: false,
      counterparty_department_id: null,
      evidence_type: null,
      invoice_no: null,
      applied_at: null,
      applied_by: null,
      manager_checked_at: null,
      manager_checked_by: null,
      approved_at: null,
      approved_by: null,
      paid_at: serverTimestamp(),
      paid_by: userId,
      completed_at: null,
      completed_by: null,
      submitted_at: null,
      submitted_by: null,
      confirmed_at: null,
      confirmed_by: null,
      rejected_at: null,
      rejected_by: null,
      reject_reason: null,
      rejected_from_status: null,
      cash_entry_id: feeCashEntryId,
      recurring_cost_id: null,
      created_by: userId,
      created_at: serverTimestamp(),
      updated_by: userId,
      updated_at: serverTimestamp(),
    });
  }

  await commitMain([transaction], () => batch.commit());

  return runFollowUps([
    // 支払いが済んだので、支払い宛ての操作依頼をまとめて既読にする(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    transaction.advance
      ? // 部門長立替: 次は経理の精算(完了確認は精算後)
        [
          '経理への通知',
          async () =>
            notifyUsers({
              userIds: await usersForNotification('accounting'),
              transactionId: transaction.id,
              message: `${transaction.code} の立替精算をお願いします(部門長が立替払い・実績計上済み)`,
              kind: 'action',
              stage: 'pay',
            }),
        ]
      : [
          transaction.source === 'recurring' ? '部門長・承認者への通知' : '承認者への通知',
          async () => {
            // 繰り返し支払いは、経理が支払ったあと事業部の部門長が確認する(2026-10-15)。部門長がいなければ承認者へ
            if (transaction.source === 'recurring') {
              const managers = recurringManagersFor({ department_id: transaction.department_id, paid_by: userId }, await fetchUsersFromServer());
              if (managers.length > 0) {
                await notifyUsers({
                  userIds: managers,
                  transactionId: transaction.id,
                  message: `${transaction.code} の繰り返し支払いを確認してください(経理が支払い済み)`,
                  kind: 'action',
                  stage: 'approve',
                });
                return;
              }
            }
            await notifyUsers({
              userIds: await usersForNotification('approver'),
              transactionId: transaction.id,
              message: `${transaction.code} の完了確認をお願いします(支払い済み・実績計上)`,
              kind: 'action',
              stage: 'confirm',
            });
          },
        ],
    // お知らせ(取引ごとに1行で上書き)。自分で支払った場合も行を最新化する(2026-08-27)
    [
      '申請者への通知',
      () =>
        notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} が支払われました(実績計上)。${
            transaction.advance ? '経理の精算待ちです' : '承認者の完了確認待ちです'
          }`,
        }),
    ],
    [
      '操作履歴の記録',
      () =>
        writeAuditLog({
          targetType: 'transaction',
          targetId: transaction.id,
          action: 'pay',
          by: userId,
          after: { amount, payment_date: input.paymentDate },
        }),
    ],
  ]);
}

// --- 立替精算(v3.7・部門長立替) --------------------------------------------

/** 経理の立替精算待ち(部門長が立替払い済みで未精算のもの) */
export async function fetchForReimbursement(): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('advance', '==', true),
      where('status', '==', 'paid'),
      where('reimbursed_at', '==', null),
      limit(100),
    ),
  );
  return snap.docs.map(toTransaction).sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
}

/**
 * 立替精算(経理)。部門長へ精算し、その日付でCFへ出金を計上する。
 * PLは部門長の支払い時に計上済み(決定: 2026-08-24)。精算後に承認者の完了確認へ進む
 */
export async function reimburse(params: {
  transaction: Transaction;
  reimbursementDate: DateStr;
  cashAccountId: string;
  userId: string;
}): Promise<FlowResult> {
  const { transaction, reimbursementDate, cashAccountId, userId } = params;
  if (transaction.status !== 'paid' || !transaction.advance) {
    throw new Error('この申請は立替精算の対象ではありません');
  }
  if (transaction.reimbursed_at) throw new Error('この申請は精算済みです');
  if (!reimbursementDate) throw new Error('精算日を入力してください');
  if (!cashAccountId) throw new Error('精算に使う口座・現金を選んでください');

  const db = getDb();
  const batch = writeBatch(db);
  const cashEntryId = newCashEntryId();
  batch.set(doc(db, 'cash_entries', cashEntryId), {
    cash_account_id: cashAccountId,
    date: reimbursementDate,
    direction: 'out',
    amount: transaction.amount,
    status: 'settled',
    description: `立替精算 ${transaction.code ?? ''}(立替者へ)`,
    counterparty: null,
    transaction_id: transaction.id,
    import_batch_id: null,
    dedup_hash: dedupHash({
      cashAccountId,
      date: reimbursementDate,
      direction: 'out',
      amount: transaction.amount,
      description: `立替精算 ${transaction.code ?? ''}`,
    }),
    matched_at: null,
    matched_by: null,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  batch.update(doc(db, COLLECTION, transaction.id), {
    reimbursed_at: serverTimestamp(),
    reimbursed_by: userId,
    reimbursement_date: reimbursementDate,
    reimbursement_cash_entry_id: cashEntryId,
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
  await commitMain([transaction], () => batch.commit());

  // 次は立替した本人が受領書に記入して添付する(v1.1 §3-B-6)
  const payerId = transaction.paid_by ?? transaction.created_by;
  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '立替した人への通知',
      () =>
        notifyUsers({
          userIds: [payerId],
          transactionId: transaction.id,
          message: `${transaction.code} の立替が精算されました(精算日: ${reimbursementDate})。受領書に記入して「受領済み」画面から添付してください`,
          kind: 'action',
          stage: 'receive',
        }),
    ],
    [
      '操作履歴の記録',
      () =>
        writeAuditLog({
          targetType: 'transaction',
          targetId: transaction.id,
          action: 'reimburse',
          by: userId,
          after: { reimbursement_date: reimbursementDate, cash_account_id: cashAccountId },
        }),
    ],
  ]);
}

/** まとめて精算の記録IDを発行する(先にファイルを保存するため) */
export function newSettlementId(): string {
  return doc(collection(getDb(), 'settlements')).id;
}

/**
 * まとめて精算(2026-09-04 発注者指示)。同じ立替者の立替をまとめて精算する。
 * - settlements に記録(一覧PDF・受領書サイン/振込明細つき)
 * - 申請ごとにCFへ出金を計上し、精算済み・受領済み(経理の画面でサイン済み)にする
 * - 受領は精算時に済むため、そのまま承認者の完了確認へ進む
 */
export async function reimburseBatch(params: {
  transactions: Transaction[];
  settlementId: string;
  settlementDate: DateStr;
  cashAccountId: string;
  method: 'cash' | 'bank_transfer';
  listPdf: StoredFile;
  evidence: StoredFile[];
  payeeId: string;
  payeeName: string;
  userId: string;
}): Promise<FlowResult> {
  const { transactions, settlementId, settlementDate, cashAccountId, method, listPdf, evidence, payeeId, payeeName, userId } = params;
  if (transactions.length === 0) throw new Error('精算する申請を選んでください');
  if (!settlementDate) throw new Error('精算日を入力してください');
  if (!cashAccountId) throw new Error('精算に使う口座・現金を選んでください');
  for (const t of transactions) {
    if (t.status !== 'paid' || !t.advance) throw new Error(`${t.code} は立替精算の対象ではありません`);
    if (t.reimbursed_at) throw new Error(`${t.code} は精算済みです`);
    if ((t.paid_by ?? t.created_by) !== payeeId) throw new Error(`${t.code} は別の立替者の申請です`);
  }

  const db = getDb();
  const batch = writeBatch(db);
  const total = transactions.reduce((s, t) => s + t.amount, 0);
  batch.set(doc(db, 'settlements', settlementId), {
    payee_id: payeeId,
    payee_name: payeeName,
    transaction_ids: transactions.map((t) => t.id),
    count: transactions.length,
    total_amount: total,
    settlement_date: settlementDate,
    cash_account_id: cashAccountId,
    method,
    list_pdf: listPdf,
    evidence,
    created_by: userId,
    created_at: serverTimestamp(),
  });
  for (const t of transactions) {
    const cashEntryId = newCashEntryId();
    batch.set(doc(db, 'cash_entries', cashEntryId), {
      cash_account_id: cashAccountId,
      date: settlementDate,
      direction: 'out',
      amount: t.amount,
      status: 'settled',
      description: `立替精算 ${t.code ?? ''}(${payeeName}へ・まとめて精算)`,
      counterparty: null,
      transaction_id: t.id,
      import_batch_id: null,
      dedup_hash: dedupHash({
        cashAccountId,
        date: settlementDate,
        direction: 'out',
        amount: t.amount,
        description: `立替精算 ${t.code ?? ''}`,
      }),
      matched_at: null,
      matched_by: null,
      created_by: userId,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
    batch.update(doc(db, COLLECTION, t.id), {
      reimbursed_at: serverTimestamp(),
      reimbursed_by: userId,
      reimbursement_date: settlementDate,
      reimbursement_cash_entry_id: cashEntryId,
      settlement_id: settlementId,
      // 受領書(サイン入り一覧PDF)/振込明細を精算時に記録する(本人の受領ステップは不要・2026-09-04)
      settlement_receipts: [listPdf, ...evidence],
      settlement_received_at: serverTimestamp(),
      settlement_received_by: payeeId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    });
  }
  await commitMain(transactions, () => batch.commit());

  let approvers: string[] | null = null;
  const approverIds = async () => (approvers ??= await usersForNotification('approver'));
  return runFollowUps(
    transactions.flatMap((t): Array<[string, () => Promise<unknown>]> => [
      ['お知らせの既読化', () => markActionDone(t.id)],
      [
        '承認者への通知',
        async () =>
          notifyUsers({
            userIds: await approverIds(),
            transactionId: t.id,
            message: `${t.code} の完了確認をお願いします(立替精算済み・${payeeName})`,
            kind: 'action',
            stage: 'confirm',
          }),
      ],
      [
        '立替した人への通知',
        () =>
          notifyUsers({
            userIds: [payeeId],
            transactionId: t.id,
            message: `${t.code} の立替が精算されました(精算日: ${settlementDate}・${method === 'cash' ? '現金' : '振込'})`,
          }),
      ],
      [
        '操作履歴の記録',
        () =>
          writeAuditLog({
            targetType: 'transaction',
            targetId: t.id,
            action: 'reimburse',
            by: userId,
            after: { reimbursement_date: settlementDate, cash_account_id: cashAccountId, settlement_id: settlementId },
          }),
      ],
    ]),
  );
}

/** ④ 完了確認(paid → completed)。承認者のみ。以降ロック */
export async function complete(params: { transaction: Transaction; userId: string }): Promise<FlowResult> {
  const { transaction, userId } = params;
  if (transaction.status !== 'paid') throw new Error('この申請は完了確認できる状態ではありません');
  if (transaction.advance && (!transaction.reimbursed_at || !transaction.settlement_received_at)) {
    throw new Error('立替の申請は、経理の精算(受領のサインまたは振込明細)が済んでから完了確認できます');
  }

  // 完了時に添付のJPEG/PNGをA4のPDFへ変換して差し替える(2026-08-26。失敗した分は元のまま)
  const pdfPatch = await convertImagesToPdf(transaction, userId);

  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      ...pdfPatch,
      status: 'completed',
      completed_at: serverTimestamp(),
      completed_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );

  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '申請者への通知',
      () =>
        notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} が完了になりました(以降ロックされます)`,
        }),
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'complete', by: userId }),
    ],
  ]);
}

/** 取消できる状態(お金が動く前のみ。仮払い済み・支払い済みは取消不可) */
export const CANCELLABLE_STATUSES = ['applied', 'manager_checked', 'approved', 'rejected'] as const;

/**
 * 申請の取消(2026-08-26)。申請者本人のみ。
 * 削除ではなく status='cancelled' にして履歴として残す(一覧に「取消」で表示)
 */
export async function cancelApplication(params: {
  transaction: Transaction;
  userId: string;
}): Promise<FlowResult> {
  const { transaction, userId } = params;
  if (transaction.created_by !== userId) throw new Error('取消できるのは申請者本人だけです');
  if (!(CANCELLABLE_STATUSES as readonly string[]).includes(transaction.status)) {
    throw new Error('この申請は取消できる状態ではありません(支払い・仮払いが始まった申請は差戻しで対応してください)');
  }

  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      status: 'cancelled',
      cancelled_at: serverTimestamp(),
      cancelled_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );
  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '承認者への通知',
      async () =>
        notifyUsers({
          userIds: await usersForNotification('approver'),
          transactionId: transaction.id,
          message: `${transaction.code} が申請者により取消されました`,
        }),
    ],
    [
      '操作履歴の記録',
      () => writeAuditLog({ targetType: 'transaction', targetId: transaction.id, action: 'cancel', by: userId }),
    ],
  ]);
}

/** 差戻し(applied〜paid → rejected)。理由必須 */
export async function reject(params: {
  transaction: Transaction;
  userId: string;
  reason: string;
}): Promise<FlowResult> {
  const { transaction, userId, reason } = params;
  if (!reason.trim()) throw new Error('差戻しの理由を入力してください');
  // 支払い前の段階だけ差し戻せる(2026-10-22 発注者決定: 支払い済みは不可。訂正は承認者の修正で行う)
  if (transaction.status === 'paid') {
    throw new Error('支払い済みの申請は差し戻せません。訂正は承認者の「修正」で行ってください');
  }
  if (!['applied', 'manager_checked', 'approved', 'cash_ready', 'received'].includes(transaction.status)) {
    throw new Error('この申請は差戻しできる状態ではありません');
  }

  await commitMain([transaction], () =>
    updateDoc(doc(getDb(), COLLECTION, transaction.id), {
      status: 'rejected',
      rejected_from_status: transaction.status,
      reject_reason: reason.trim(),
      rejected_at: serverTimestamp(),
      rejected_by: userId,
      // 差戻し履歴(v3.8)。詳細画面に時系列で表示する。arrayUnionはserverTimestamp不可のためTimestamp.now()
      reject_history: arrayUnion({
        at: Timestamp.now(),
        by: userId,
        reason: reason.trim(),
        from_status: transaction.status,
      }),
      updated_at: serverTimestamp(),
      updated_by: userId,
    }),
  );

  return runFollowUps([
    // アクション完了: 操作依頼のバッジを消す(2026-08-27)
    ['お知らせの既読化', () => markActionDone(transaction.id)],
    [
      '申請者への通知',
      () =>
        notifyUsers({
          userIds: [transaction.created_by],
          transactionId: transaction.id,
          message: `${transaction.code} が差戻されました: ${reason.trim()} — 内容を確認して再申請してください`,
        }),
    ],
    [
      '操作履歴の記録',
      () =>
        writeAuditLog({
          targetType: 'transaction',
          targetId: transaction.id,
          action: 'reject',
          by: userId,
          after: { reason: reason.trim() },
        }),
    ],
  ]);
}
